/**
 * V118-0-4 / ADR-0117 §3: `intake.extract_obligations` — one extraction over one stored statute text.
 *
 *   1. split    a text longer than one call's window is split at section headings (each part ≤ PART_MAX_CHARS)
 *   2. extract  per part, the gateway call with a strict schema; invalid output is retried with the raw validation
 *               errors, at most MAX_ATTEMPTS calls; an unreadable reply is a failure, never an empty set (#480)
 *   3. assemble code locates every quote (span-locator), resolves sections, derives each record's control,
 *               de-duplicates across parts, assigns ids in text order and lists uncovered sections
 *
 * The model returns quotes only; offsets, controls and coverage are code (ADR-0039). Pure: the gateway is injected,
 * nothing is stored here (statute-obligation-set-service stores the result).
 */
import { defineTemplate, field } from '../../egress/data-classes.js';
import { ACTOR_CLASSES, BINDS_WHOM_SCOPES, DEONTIC_KINDS } from '../../data/intake-enums.js';
import { INTAKE_FEWSHOT_EXAMPLES } from '../../data/intake-fewshot.js';
import { normalizeForGrounding } from '../statute-fetch-policy.js';
import { buildNormalizedIndex, locateQuote, MIN_SHORT_SPAN_CHARS, snapToSentence } from './span-locator.js';
import { extractionErrors, OBLIGATION_SET_SCHEMA_VERSION, RawExtractionSchema, } from './obligation-set.js';
export const MAX_ATTEMPTS = 3;
// v2 (2026-11-01 eval): smaller parts and a larger output budget; a long part ran out of tokens mid-JSON.
export const PART_MAX_CHARS = 20_000;
export const MAX_OUTPUT_TOKENS = 12_000;
export const INTAKE_EXTRACT_INSTRUCTIONS = [
    'You read the text of one law (statute, bill, regulation or rule) and list every obligation it states, as JSON.',
    '',
    'LAW: {title}',
    'JURISDICTION: {jurisdiction}',
    '',
    'SCOPE: only obligations about artificial intelligence, automated decision systems, algorithms, bots, synthetic or',
    'AI-generated content, or the data and decisions those systems use or make. Leave out duties that do not touch them',
    '(e.g. budget, tax, licensing or procedure provisions). If LAW names one part or section of a larger bill',
    '(e.g. "Part U"), extract only from that part.',
    '',
    'Return `obligations`: one entry per distinct duty, prohibition, permission or exemption in scope. For each:',
    '- `section`: the section or subsection citation as the text writes it (e.g. "§ 17941(b)"), or null.',
    '- `excerpt`: the operative sentence, COPIED EXACTLY from TEXT, character for character (at least 40 characters).',
    '  Never paraphrase, shorten, reorder or fix it. If no sentence in TEXT states the duty, leave the duty out.',
    '- `deontic`: one of {deontic_kinds}. A permission ("may") binds no one; an exemption lifts a duty.',
    '- `binds_whom`: `scope` (one of {scopes}: private = businesses or persons, public = government bodies, mixed = both),',
    '  `actor_class` (EXACTLY one of {actor_classes}; use other when none fits; platform = an online service that hosts other people\'s content,',
    '  public_body = a government agency), and `exclusions`: who the text says is NOT bound, each as {text, quote}.',
    '- `obligation`: one plain sentence saying who must do what.',
    '- `trigger_conditions`: when the duty applies, each as {text, quote}.',
    '- `unless`: every exception, safe harbour or "does not apply" carve-out that limits THIS duty, each as {text, quote}.',
    '- `thresholds`: [] or typed values: {"kind":"review_every_days","value":N}, {"kind":"review_every_hours","value":N},',
    '  {"kind":"metric_min","metric":"<name>","value":N}, {"kind":"metric_max","metric":"<name>","value":N}.',
    '- `deadlines`: dates or periods the duty must meet, each as {date: "YYYY-MM-DD" or null, text, quote}.',
    '- `effective_date`: {date: "YYYY-MM-DD", quote} only when TEXT states when the duty starts; otherwise null (never a date without its quote).',
    '- `duty`: the matching duty from DUTIES below, or a new snake_case name when none fits; null for a permission.',
    '- `new_control`: REQUIRED when `duty` is not in DUTIES: {title, definition} of the control that would meet it; else null.',
    '- `fixtures`: 1 to 3 short cases, each {description, answers (profile answers as JSON), expect_applies (true/false)}.',
    '- `confidence`: your estimate in [0, 1].',
    'Also return `applicability`: the clauses that say who or what the law covers, each as {text, quote};',
    '`missing_concepts`: concepts the law turns on that DUTIES cannot express; `cross_references`: other laws it cites.',
    '',
    'Every `quote` and `excerpt` must be copied exactly from the text. If you cannot quote it, leave it out.',
    'Never invent duties the text does not state.',
    '',
    'DUTIES:',
    '{duty_vocabulary}',
    '',
    'JURISDICTION TERMS:',
    '{jurisdiction_terms}',
    '',
    'WORKED EXAMPLES (other laws; the granularity and fields to match, not text to reuse):',
    '{examples}',
    '',
    '{repair_errors}',
    'TEXT:',
    '{document_text}',
].join('\n');
/** id@version is the prompt version recorded on every run (and part of the extractor version). */
export const INTAKE_EXTRACT_INPUT_TEMPLATE = defineTemplate('intake.extract_obligations.input', 'v4', INTAKE_EXTRACT_INSTRUCTIONS, [
    'title', 'jurisdiction', 'deontic_kinds', 'scopes', 'actor_classes', 'duty_vocabulary', 'jurisdiction_terms', 'examples', 'repair_errors', 'document_text',
]);
export const INTAKE_EXTRACT_TEMPLATE = defineTemplate('intake.extract_obligations', 'v4', 'List the obligations of a law as JSON, quoting the text.', []);
/** V118-0-9: the worked examples, compact (three frozen few-shot laws; never scored). */
export const INTAKE_EXAMPLES_TEXT = INTAKE_FEWSHOT_EXAMPLES
    .map((e) => `LAW: ${e.law}\n${JSON.stringify({ obligations: e.obligations })}`).join('\n\n');
/** Words that mark a section as in scope for the coverage pass (V118-0-9). */
const AI_TERMS = /\b(artificial intelligence|automated decision|algorithm|machine learning|\bbots?\b|chatbot|synthetic|deepfake|generative|profiling|AI)\b/i;
export const PART_OVERLAP_CHARS = 1_500;
export const COVERAGE_PASS_MAX_CHARS = 20_000;
/**
 * The extractor version keys `statute_obligation_sets` (ADR-0117 §4). Bump the template version for a prompt change;
 * the schema version for a record change. A provider model alias change must bump it too.
 */
export const EXTRACTOR_VERSION = `${INTAKE_EXTRACT_INPUT_TEMPLATE.id}@${INTAKE_EXTRACT_INPUT_TEMPLATE.version}+s${OBLIGATION_SET_SCHEMA_VERSION}`;
const SCHEMA_HINT = {
    obligations: [{
            section: '§ 1(a) or null', excerpt: 'exact sentence from the text', deontic: 'obligation',
            binds_whom: { scope: 'private', actor_class: 'deployer', exclusions: [{ text: 'who is not bound', quote: 'exact words' }] },
            obligation: 'one plain sentence', trigger_conditions: [{ text: 'when', quote: 'exact words' }],
            unless: [{ text: 'exception', quote: 'exact words' }], thresholds: [],
            deadlines: [{ date: 'YYYY-MM-DD or null', text: 'within 48 hours', quote: 'exact words' }],
            effective_date: { date: 'YYYY-MM-DD', quote: 'exact words' }, duty: 'snake_case', new_control: null,
            fixtures: [{ description: 'a covered deployer', answers: {}, expect_applies: true }], confidence: 0.8,
        }],
    applicability: [{ text: 'who the law covers', quote: 'exact words' }], missing_concepts: [], cross_references: [],
};
/** Every attempt failed: the provider refused, threw, or kept returning output the schema rejects. */
export class ObligationExtractionError extends Error {
    attempts;
    constructor(attempts) {
        super(`obligation extraction failed: ${attempts.map((a) => `part ${a.part} attempt ${a.attempt}: ${a.error}`).join('; ')}`.slice(0, 1200));
        this.attempts = attempts;
        this.name = 'ObligationExtractionError';
    }
}
// ── 1. split ────────────────────────────────────────────────────────────────
/** A line that starts a section: "§ 12", "Section 3.", "Sec. 4", "SECTION 5", "Article 6", "6-1-1701.", "17941." */
const HEADING = /^[ \t]*(?:§+\s*\d|(?:section|sec\.)\s+\d|article\s+\d|\d+(?:[.-]\d+)+\.?\s|\d{3,}\.\s)/i;
/** Section headings in text order; each runs to the next heading. */
export function findHeadings(text) {
    const out = [];
    let at = 0;
    for (const line of text.split('\n')) {
        if (HEADING.test(line))
            out.push({ start: at, end: text.length, label: line.trim().slice(0, 120), numbers: numbersIn(line).slice(0, 3) });
        at += line.length + 1;
    }
    for (let i = 0; i < out.length - 1; i++)
        out[i].end = out[i + 1].start;
    return out;
}
function numbersIn(s) {
    return [...String(s).matchAll(/\d+(?:[.-]\d+)*/g)].map((m) => m[0]).filter((n) => n.length >= 1);
}
/**
 * Parts of at most `max` characters, cut at headings where possible, else at a blank line, else hard. V118-0-9: each
 * part after the first also carries the `overlap` characters before its cut (from a line start), so a duty that spans
 * a cut is read whole once; the duplicates this makes are merged at assembly.
 */
export function splitAtSections(text, max = PART_MAX_CHARS, overlap = PART_OVERLAP_CHARS) {
    if (text.length <= max)
        return [{ start: 0, text }];
    const cuts = new Set([0, ...findHeadings(text).map((h) => h.start)]);
    const parts = [];
    let start = 0;
    while (start < text.length) {
        let end = Math.min(text.length, start + max);
        if (end < text.length) {
            const best = [...cuts].filter((c) => c > start && c <= end).pop()
                ?? (text.lastIndexOf('\n\n', end) > start ? text.lastIndexOf('\n\n', end) + 2 : end);
            end = best;
        }
        const lead = start === 0 || overlap <= 0 ? start : Math.max(0, text.lastIndexOf('\n', start - overlap) + 1);
        parts.push({ start: lead, text: text.slice(lead, end) });
        start = end;
    }
    return parts;
}
/** Sections with no record whose text names an AI term: the coverage pass reads them again (V118-0-9). */
export function uncoveredAiSections(text, set) {
    const covered = set.obligations.map((o) => o.excerpt_span).filter((s) => Boolean(s));
    return findHeadings(text).filter((h) => !covered.some((s) => s.start >= h.start && s.start < h.end) && AI_TERMS.test(text.slice(h.start, h.end)));
}
// ── 2. extract ──────────────────────────────────────────────────────────────
async function extractPart(llm, input, partText, part, failures, preface = '') {
    const leg = (v) => field('research.legislation_text', v);
    const vocab = (v) => field('rules.vocabulary', v);
    let repair = preface;
    // v2: a schema-valid answer whose excerpts code cannot find gets one repair turn naming them; the answer with fewer
    // unlocated excerpts is kept. Total calls per part stay within MAX_ATTEMPTS.
    let best = null;
    const index = buildNormalizedIndex(input.text);
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
            const r = await llm.run({
                task: input.task ?? 'intake.extract_obligations', template: INTAKE_EXTRACT_TEMPLATE, inputTemplate: INTAKE_EXTRACT_INPUT_TEMPLATE,
                slots: {
                    title: leg(input.title), jurisdiction: leg(input.jurisdiction ?? '(unknown)'),
                    deontic_kinds: vocab(DEONTIC_KINDS.join(', ')), scopes: vocab(BINDS_WHOM_SCOPES.join(', ')),
                    actor_classes: vocab(ACTOR_CLASSES.join(', ')),
                    duty_vocabulary: vocab([...input.duties].sort().join(', ') || '(none)'),
                    jurisdiction_terms: vocab(input.jurisdiction_terms?.trim() || '(none declared)'),
                    examples: leg(INTAKE_EXAMPLES_TEXT),
                    repair_errors: vocab(repair),
                    document_text: leg(partText),
                },
                schemaHint: SCHEMA_HINT, options: { maxTokens: MAX_OUTPUT_TOKENS, temperature: 0 },
            });
            if (r.status !== 'ok') {
                failures.push({ part, attempt, error: `the AI gateway refused the call (${r.reason})` });
                if (best)
                    return best; // keep a valid earlier answer
                throw new ObligationExtractionError(failures); // a refusal will not change on retry
            }
            const errors = extractionErrors(r.output);
            if (errors.length === 0) {
                const out = RawExtractionSchema.parse(r.output);
                const misses = out.obligations.map((o, i) => ({ i, o })).filter(({ o }) => !locateQuote(index, o.excerpt).ok);
                const candidate = { out, run_id: r.run_id, provider_id: r.provider_id, model_id: r.model_id, unlocated: misses.length,
                    located: out.obligations.length - misses.length };
                // v3: more located excerpts wins, then fewer unlocated; an answer that drops everything can never beat one
                // that found something (v2 preferred fewest misses, which an empty answer always has).
                if (!best || candidate.located > best.located || (candidate.located === best.located && candidate.unlocated < best.unlocated))
                    best = candidate;
                if (best.unlocated === 0 || attempt === MAX_ATTEMPTS)
                    return best;
                repair = ['YOUR PREVIOUS ANSWER QUOTED TEXT THAT IS NOT IN THE TEXT. For each excerpt below, copy the exact sentence',
                    'from TEXT, character for character, and answer again with the whole JSON (or drop the obligation if no sentence states it):',
                    ...misses.slice(0, 20).map(({ i, o }) => `- obligations.${i}.excerpt (you wrote: "${o.excerpt.slice(0, 160)}")`), ''].join('\n');
                continue;
            }
            failures.push({ part, attempt, error: `schema: ${errors.slice(0, 5).join(' | ')}` });
            repair = ['YOUR PREVIOUS ANSWER WAS REJECTED. Fix these errors and answer again with the whole JSON:', ...errors.map((e) => `- ${e}`), ''].join('\n');
        }
        catch (err) {
            if (err instanceof ObligationExtractionError)
                throw err;
            if (err instanceof Error && err.name === 'PlatformScopeViolation')
                throw err;
            failures.push({ part, attempt, error: (err instanceof Error ? err.message : String(err)).slice(0, 300) });
        }
    }
    if (best)
        return best;
    throw new ObligationExtractionError(failures);
}
// ── 3. assemble ─────────────────────────────────────────────────────────────
/** Locate a short span; a missing quote, or one code cannot find, is listed in missing_source (cite or abstain). */
function locateShort(index, quote) {
    return quote ? locateQuote(index, quote, { minChars: MIN_SHORT_SPAN_CHARS }) : null;
}
function spanned(index, v, path, missing) {
    const r = locateShort(index, v.quote);
    if (!r?.ok)
        missing.push(path);
    return { text: v.text, quote: v.quote, span: r?.ok ? r.span : null };
}
function spannedDate(index, v, path, missing) {
    const r = locateShort(index, v.quote);
    if (!r?.ok)
        missing.push(path);
    return { date: v.date, quote: v.quote, span: r?.ok ? r.span : null };
}
/** "bot_disclosure_records" → "Bot disclosure records". */
const titleOfDuty = (duty) => duty.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
/** The heading whose numbers include the citation's first number (e.g. "17941" of "§ 17941(b)"). */
function sectionRange(headings, section) {
    const first = numbersIn(section ?? '')[0];
    if (!first)
        return null;
    const h = headings.find((x) => x.numbers.includes(first));
    return h ? { start: h.start, end: h.end } : null;
}
function toRecord(raw, index, headings, controlByDuty, known) {
    const missing = [];
    const range = sectionRange(headings, raw.section);
    const found = locateQuote(index, raw.excerpt);
    // V118-0-8 (owner 2026-11-02): an excerpt code cannot find is snapped to the nearest real sentence of the cited
    // section (or the whole text), keeping the model's wording; below the threshold it stays missing_source.
    const snap = found.ok ? null : snapToSentence(index.raw, raw.excerpt, range ?? undefined) ?? (range ? snapToSentence(index.raw, raw.excerpt) : null);
    const ex = found.ok ? found : snap ? { ok: true, span: snap.span, occurrences: 1 } : found;
    if (!ex.ok)
        missing.push('excerpt');
    if (raw.section && ex.ok && range && !(ex.span.start >= range.start && ex.span.end <= range.end))
        missing.push('section');
    const binding = raw.deontic === 'obligation' || raw.deontic === 'prohibition';
    const existing = raw.duty ? controlByDuty.get(raw.duty) ?? null : null;
    // v2: a new duty without a proposed control gets one drafted from its own sentence, so no binding record is left
    // without a control (the reviewer edits it; ADR-0118). A known duty with no single mapped control stays null.
    const control = !binding || !raw.duty ? null
        : existing ? { existing }
            : known.has(raw.duty) ? null
                : { new: raw.new_control ?? { title: titleOfDuty(raw.duty), definition: raw.obligation } };
    return {
        section: raw.section,
        section_range: range,
        excerpt: snap ? snap.span.quote : raw.excerpt,
        excerpt_span: ex.ok ? ex.span : null,
        excerpt_model: snap ? raw.excerpt : null,
        snapped: snap ? ['excerpt'] : [],
        deontic: raw.deontic,
        binds_whom: {
            scope: raw.binds_whom.scope, actor_class: raw.binds_whom.actor_class,
            exclusions: raw.binds_whom.exclusions.map((x, i) => spanned(index, x, `binds_whom.exclusions.${i}`, missing)),
        },
        obligation: raw.obligation,
        trigger_conditions: raw.trigger_conditions.map((x, i) => spanned(index, x, `trigger_conditions.${i}`, missing)),
        unless: raw.unless.map((x, i) => spanned(index, x, `unless.${i}`, missing)),
        law_parameters: {
            thresholds: raw.thresholds,
            deadlines: raw.deadlines.map((d, i) => ({ ...spannedDate(index, d, `law_parameters.deadlines.${i}`, missing), text: d.text })),
        },
        effective_date: raw.effective_date ? spannedDate(index, raw.effective_date, 'effective_date', missing) : null,
        duty: binding ? raw.duty : null,
        control,
        vocabulary_terms: binding && raw.duty ? [raw.duty] : [],
        fixtures: raw.fixtures,
        confidence: raw.confidence,
        missing_source: missing,
        checker_findings: [],
    };
}
const dedupeKey = (r) => `${numbersIn(r.section ?? '')[0] ?? ''}|${normalizeForGrounding(r.excerpt)}`;
/** Build the set from the parsed parts. Pure; exported for tests. */
export function assembleObligationSet(input, parts, meta) {
    const index = buildNormalizedIndex(input.text);
    const headings = findHeadings(input.text);
    const known = new Set(input.duties);
    const controlByDuty = input.controlByDuty ?? new Map();
    const seen = new Set();
    const records = [];
    const applicability = [];
    const missingConcepts = new Set();
    const crossRefs = new Set();
    const appMissing = [];
    for (const p of parts) {
        for (const raw of p.obligations) {
            const k = dedupeKey(raw);
            if (seen.has(k))
                continue;
            seen.add(k);
            records.push(toRecord(raw, index, headings, controlByDuty, known));
        }
        p.applicability.forEach((a, i) => applicability.push(spanned(index, a, `applicability.${i}`, appMissing)));
        p.missing_concepts.forEach((c) => missingConcepts.add(c));
        p.cross_references.forEach((c) => crossRefs.add(c));
    }
    records.sort((a, b) => (a.excerpt_span?.start ?? Number.MAX_SAFE_INTEGER) - (b.excerpt_span?.start ?? Number.MAX_SAFE_INTEGER));
    const covered = new Set(records.flatMap((r) => (r.excerpt_span ? headings.filter((h) => r.excerpt_span.start >= h.start && r.excerpt_span.start < h.end) : [])));
    return {
        schema_version: OBLIGATION_SET_SCHEMA_VERSION,
        instrument_key: input.instrument_key,
        jurisdiction: input.jurisdiction,
        text_sha256: input.text_sha256,
        extractor_version: EXTRACTOR_VERSION,
        source: input.source,
        provider_id: meta.provider_id,
        model_id: meta.model_id,
        run_ids: meta.run_ids,
        applicability,
        missing_concepts: [...missingConcepts],
        cross_references: [...crossRefs],
        coverage: { headings: headings.length, uncovered_sections: headings.filter((h) => !covered.has(h)).map((h) => h.label) },
        obligations: records.map((r, i) => ({ obligation_id: `${input.instrument_key}-${i + 1}`, ...r })),
    };
}
/** Run the extraction (ADR-0117 §3). Throws `ObligationExtractionError` when any part fails every attempt. */
export async function extractObligationSet(llm, input) {
    const failures = [];
    const parsed = [];
    const runIds = [];
    let provider = null, model = null;
    const parts = splitAtSections(input.text);
    for (let i = 0; i < parts.length; i++) {
        const r = await extractPart(llm, input, parts[i].text, i + 1, failures);
        parsed.push(r.out);
        runIds.push(r.run_id);
        provider = r.provider_id;
        model = r.model_id;
    }
    const first = assembleObligationSet(input, parsed, { run_ids: runIds, provider_id: provider, model_id: model });
    // V118-0-9 coverage pass: sections that name an AI term but yielded no record are read once more, together (one
    // call, capped). A failure here keeps the first answer: the pass can only add records.
    const gaps = uncoveredAiSections(input.text, first);
    if (gaps.length === 0)
        return first;
    let joined = '';
    for (const h of gaps) {
        const sec = input.text.slice(h.start, h.end);
        if (joined.length + sec.length > COVERAGE_PASS_MAX_CHARS)
            break;
        joined += (joined ? '\n\n' : '') + sec;
    }
    if (!joined)
        return first;
    try {
        const r = await extractPart(llm, input, joined, parts.length + 1, failures, 'THESE SECTIONS HAD NO OBLIGATION IN A FIRST READING. List any in-scope obligation they state; return an empty list if none.\n');
        parsed.push(r.out);
        runIds.push(r.run_id);
    }
    catch (err) {
        if (err instanceof Error && err.name === 'PlatformScopeViolation')
            throw err;
        return first;
    }
    return assembleObligationSet(input, parsed, { run_ids: runIds, provider_id: provider, model_id: model });
}
//# sourceMappingURL=obligation-extraction.js.map